/**
 * Pairing two equal sets so that the total cost is as low as it goes.
 *
 * The assignment problem, in the shortest-path form of the Hungarian
 * algorithm. `stroke-assignment.ts` is the only caller and the only place the
 * answer means anything, and this is here so that the arithmetic and the
 * question it answers are two modules.
 *
 * Exact rather than greedy. Greedy comes within a few hundredths over stroke
 * costs and it is not a bound on the true optimum, so it can pair two strokes
 * badly and come out dearer than pairing them in the order they were written.
 * A reader who wrote a character properly would then be told their stroke
 * order was wrong.
 *
 * The cost is the cube of the set size. The busiest character the handwriting
 * index holds has 36 strokes, and a marked card asks this at most twice.
 */

/** The bookkeeping one pass of {@link cheapestAssignment} reads and writes. */
interface Augmenting {
  /** What each row costs against each column. */
  readonly costs: readonly (readonly number[])[];

  /** The dual for the rows, which is what keeps a pass off the whole matrix. */
  readonly rowShift: Float64Array;

  /** The dual for the columns. */
  readonly columnShift: Float64Array;

  /** Which row holds each column, where slot nought is the pass's free row. */
  readonly takenBy: Int32Array;

  /** Which column each column was reached from, for walking the path back. */
  readonly cameFrom: Int32Array;
}

/**
 * The cheapest total cost of pairing each row with one column.
 *
 * @param costs - A square matrix of what each row costs against each column.
 * @returns The total cost of the cheapest pairing.
 */
export function cheapestAssignment(
  costs: readonly (readonly number[])[],
): number {
  return cheapestPairing(costs).reduce(
    (total, column, row) =>
      total + ((costs[row] as readonly number[])[column] as number),
    0,
  );
}

/**
 * The cheapest pairing itself: the column each row is given.
 *
 * What `stroke-comparison.ts` reads to say which of the character's strokes
 * each drawn stroke was taken for. The total above is the same pairing summed.
 *
 * The arrays are one longer than the matrix and index from one, which is what
 * the method's own bookkeeping wants. Slot nought is the free row each pass
 * starts from rather than a row of the matrix.
 *
 * @param costs - A square matrix of what each row costs against each column.
 * @returns For each row in order, the column it is paired with.
 */
export function cheapestPairing(
  costs: readonly (readonly number[])[],
): readonly number[] {
  const size = costs.length;
  const state: Augmenting = {
    costs,
    rowShift: new Float64Array(size + 1),
    columnShift: new Float64Array(size + 1),
    takenBy: new Int32Array(size + 1),
    cameFrom: new Int32Array(size + 1),
  };

  for (let row = 1; row <= size; row += 1) {
    state.takenBy[0] = row;
    augment(state);
  }

  const paired = Array.from({ length: size }, () => 0);

  for (let column = 1; column <= size; column += 1) {
    paired[(state.takenBy[column] as number) - 1] = column - 1;
  }

  return paired;
}

/**
 * Finds the cheapest path from the new row to a column nothing has taken.
 *
 * Each pass adds one row to the pairing, handing columns over along the way.
 */
function augment(state: Augmenting): void {
  const size = state.costs.length;
  const best = new Float64Array(size + 1).fill(Number.POSITIVE_INFINITY);
  const settled = new Uint8Array(size + 1);
  let column = 0;

  do {
    settled[column] = 1;

    const found = nearestColumn({ state, best, settled, from: column });

    shiftBy({ state, best, settled, by: found.gap });
    column = found.column;
  } while (state.takenBy[column] !== 0);

  reassign(state, column);
}

/**
 * The unsettled column the row at `from` reaches most cheaply.
 *
 * Every column it improves on records `from` as where it was reached from,
 * which is the path {@link reassign} walks back afterwards.
 */
function nearestColumn(properties: {
  state: Augmenting;
  best: Float64Array;
  settled: Uint8Array;
  from: number;
}): { column: number; gap: number } {
  const { state, best, settled, from } = properties;
  const row = state.takenBy[from] as number;
  const against = state.costs[row - 1] as readonly number[];
  let gap = Number.POSITIVE_INFINITY;
  let column = 0;

  for (let at = 1; at <= state.costs.length; at += 1) {
    if (settled[at] === 1) {
      continue;
    }

    const reduced =
      (against[at - 1] as number) -
      (state.rowShift[row] as number) -
      (state.columnShift[at] as number);

    if (reduced < (best[at] as number)) {
      best[at] = reduced;
      state.cameFrom[at] = from;
    }

    if ((best[at] as number) < gap) {
      gap = best[at] as number;
      column = at;
    }
  }

  return { column, gap };
}

/** Moves the duals along by what the last step cost. */
function shiftBy(properties: {
  state: Augmenting;
  best: Float64Array;
  settled: Uint8Array;
  by: number;
}): void {
  const { state, best, settled, by } = properties;

  for (let at = 0; at <= state.costs.length; at += 1) {
    if (settled[at] === 1) {
      const row = state.takenBy[at] as number;

      state.rowShift[row] = (state.rowShift[row] as number) + by;
      state.columnShift[at] = (state.columnShift[at] as number) - by;
    } else {
      best[at] = (best[at] as number) - by;
    }
  }
}

/** Walks the path back, handing each column to the row that reached it. */
function reassign(state: Augmenting, from: number): void {
  let column = from;

  while (column !== 0) {
    const previous = state.cameFrom[column] as number;

    state.takenBy[column] = state.takenBy[previous] as number;
    column = previous;
  }
}
